import { useHistory } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import ImageLoader from '../ImageLoader';
import { LoaderIcon } from '../../utils/svg';
import { buildSafeLink, isEmptyData } from '../../utils/helper';

const DRAG_THRESHOLD = 10; // px, toleransi gerakan sebelum dianggap "drag" bukan "klik"

const Banner = (props) => {
	const { progress, banners = [], isLoading = false, isShowProgress = true, containerClassName = 'w-full relative mb-8 h-[270px]', imageClassName = 'w-full h-full max-h-64 object-fill !rounded-none', disabled = false, isHidden = true } = props;
	const [activeIndex, setActiveIndex] = useState(0);
	const history = useHistory();

	const pointerStart = useRef({ x: 0, y: 0 });
	const hasDraggedRef = useRef(false);

	const autoplay = Autoplay({
		delay: 5000,
		stopOnInteraction: true,
		stopOnMouseEnter: false,
	});

	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			active: !disabled,
			loop: false,
			align: 'start',
			slidesToScroll: 1,
			watchDrag: !disabled,
		},
		[autoplay],
	);

	const itemsToRender = useMemo(() => {
		if (isLoading || banners.length === 0) return [];

		return banners.map((item) => ({
			...item,
			banner_link: buildSafeLink(item.banner_link),
		}));
	}, [isLoading, banners]);

	useEffect(() => {
		if (!emblaApi) return;

		const onSelect = () => {
			setActiveIndex(emblaApi.selectedScrollSnap());
		};

		emblaApi.on('select', onSelect);

		onSelect();

		return () => {
			emblaApi.off('select', onSelect);
		};
	}, [emblaApi]);

	const handlePointerDown = (e) => {
		hasDraggedRef.current = false;
		const point = e.touches ? e.touches[0] : e;
		pointerStart.current = { x: point.clientX, y: point.clientY };
	};

	const handlePointerMove = (e) => {
		const point = e.touches ? e.touches[0] : e;
		const dx = Math.abs(point.clientX - pointerStart.current.x);
		const dy = Math.abs(point.clientY - pointerStart.current.y);

		if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
			hasDraggedRef.current = true;
		}
	};

	const handleSlideClick = (link) => {
		if (hasDraggedRef.current) return; // baru saja drag, bukan tap murni — abaikan klik
		if (isEmptyData(link)) return;
		history.push(link);
	};

	return (
		<div className={`${containerClassName} ${isEmptyData(itemsToRender) && isHidden && 'hidden'}`}>
			{isLoading ? (
				<div className='w-full h-full flex justify-center items-center'>
					<LoaderIcon />
				</div>
			) : (
				<>
					<div
						className='overflow-hidden'
						ref={emblaRef}
						onMouseEnter={() => autoplay.stop()}
						onMouseLeave={() => autoplay.play()}
						onPointerDown={handlePointerDown}
						onPointerMove={handlePointerMove}
						onTouchStart={handlePointerDown}
						onTouchMove={handlePointerMove}
						style={{ touchAction: 'pan-y' }}>
						<div className='flex'>
							{!isEmptyData(itemsToRender) ? (
								itemsToRender.map((item, index) => (
									<div key={index} className='flex-[0_0_100%] relative h-full cursor-pointer' onClick={() => handleSlideClick(item.banner_link)}>
										<ImageLoader src={item.banner_image} alt={`banner-${index}`} className={imageClassName} draggable={false} />
									</div>
								))
							) : (
								<div className='flex-[0_0_100%] relative h-full'>
									<ImageLoader className={imageClassName} draggable={false} />
								</div>
							)}
						</div>
					</div>
					<div
						className={`${isShowProgress ? 'absolute' : 'hidden'} left-1/2 -translate-x-1/2 bottom-8 flex gap-2`}
						style={{
							transition: 'background-color .15s linear',
							zIndex: progress > 0.95 ? '0' : '1',
						}}>
						{itemsToRender.map((_, i) => (
							<div key={i} className={`h-2 w-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-white' : 'bg-white/40'}`} />
						))}
					</div>
				</>
			)}
		</div>
	);
};

export default Banner;
